import { EU_AUTH_STRINGS } from '@/components/auth/eu-strings';
import type { EuAppLanguage } from '@/i18n/eu-locales';

// Auth-screen copy — Swedish + English, picked from the app's resolved locale
// (saved language setting, else the browser's). The product is Swedish-first
// but not Swedish-only; the auth door greets people in their own language.
// "AI made right" is the brand tagline and stays as-is in both.

const sv = {
  tagline: 'AI made right',
  titleSignup: 'Skapa ett konto',
  titleLogin: 'Välkommen tillbaka',
  introSignup: 'Spara dina chattar och få en assistent som minns dig.',
  google: 'Fortsätt med Google',
  divider: 'eller',
  showPassword: 'Visa lösenord',
  hidePassword: 'Dölj lösenord',
  submitSignup: 'Skapa konto',
  submitLogin: 'Logga in',
  switchToLoginPrefix: 'Har du redan ett konto?',
  switchToLogin: 'Logga in',
  switchToSignupPrefix: 'Ny på poneva?',
  switchToSignup: 'Skapa konto',
  close: 'Stäng',
  verificationTitle: 'Kolla din inkorg',
  verificationBody: 'Vi skickade en säker länk till',
  verificationHint:
    'Öppna länken i mejlet för att skapa kontot – det går bra på vilken enhet som helst. Din inbjudan väntar under tiden.',
  verificationAgain: 'Använd en annan e-postadress',
  verifying: 'Bekräftar din e-post och skapar kontot…',
  confirmPasswordTitle: 'Bekräfta ditt lösenord',
  confirmPasswordBody:
    'Din e-post är bekräftad. Du öppnade länken i en annan webbläsare, så ange lösenordet du valde för att skapa kontot och logga in här.',
  confirmPasswordPlaceholder: 'Ditt lösenord',
  confirmPasswordSubmit: 'Skapa konto och logga in',
  errors: {
    email_taken: 'Den e-postadressen är redan registrerad.',
    bad_credentials: 'Fel e-post eller lösenord.',
    rate_limited: 'För många försök – vänta en stund och försök igen.',
    invalid_input: 'Ange en giltig e-post och ett lösenord på minst 15 tecken.',
    cancelled: 'Inloggningen avbröts.',
    expired: 'Inloggningen tog för lång tid – försök igen.',
    unverified_email: 'Ditt Google-konto har ingen verifierad e-postadress.',
    account_link_required:
      'Logga in med ditt befintliga lösenord en gång för att koppla Google. Båda inloggningssätten fortsätter fungera.',
    account_link_conflict:
      'Google-kontot kunde inte kopplas till det här kontot. Börja om med Google-inloggningen.',
    account_link_expired: 'Tiden för att koppla Google gick ut. Börja om med Google-inloggningen.',
    email_verification_required:
      'Verifiera e-postadressen separat innan du använder det Google-kontot.',
    invalid_invite: 'Inbjudan är ogiltig, har gått ut eller har redan använts.',
    verification_expired: 'Verifieringslänken är ogiltig, har gått ut eller har redan använts.',
    verification_password_required:
      'Ange lösenordet du valde vid registreringen för att slutföra här.',
    verification_wrong_password: 'Fel lösenord. Ange lösenordet du valde vid registreringen.',
    oauth_failed: 'Det gick inte att logga in med Google. Försök igen.',
    oauth_unavailable: 'Google-inloggning är inte tillgänglig just nu.',
    server_unavailable: 'Tjänsten är inte tillgänglig just nu. Försök igen om en stund.',
    logout_incomplete:
      'Den här enheten kan fortfarande vara inloggad när appen startar igen. Logga i så fall ut igen.',
    generic: 'Något gick fel. Försök igen.',
  } as Record<string, string>,
};

export type AuthStrings = typeof sv;

const en: AuthStrings = {
  tagline: 'AI made right',
  titleSignup: 'Create your account',
  titleLogin: 'Welcome back',
  introSignup: 'Keep your chats and get an assistant that remembers you.',
  google: 'Continue with Google',
  divider: 'or',
  showPassword: 'Show password',
  hidePassword: 'Hide password',
  submitSignup: 'Create account',
  submitLogin: 'Log in',
  switchToLoginPrefix: 'Already have an account?',
  switchToLogin: 'Log in',
  switchToSignupPrefix: 'New to poneva?',
  switchToSignup: 'Create account',
  close: 'Close',
  verificationTitle: 'Check your inbox',
  verificationBody: 'We sent a secure link to',
  verificationHint:
    'Open the email link to create your account — any device works. Your invitation waits in the meantime.',
  verificationAgain: 'Use another email address',
  verifying: 'Verifying your email and creating your account…',
  confirmPasswordTitle: 'Confirm your password',
  confirmPasswordBody:
    'Your email is verified. You opened the link in a different browser, so enter the password you chose to create your account and log in here.',
  confirmPasswordPlaceholder: 'Your password',
  confirmPasswordSubmit: 'Create account and log in',
  errors: {
    email_taken: 'That email is already registered.',
    bad_credentials: 'Wrong email or password.',
    rate_limited: 'Too many attempts — wait a moment and try again.',
    invalid_input: 'Enter a valid email and a password of at least 15 characters.',
    cancelled: 'Sign-in was cancelled.',
    expired: 'Sign-in took too long — please try again.',
    unverified_email: 'Your Google account has no verified email address.',
    account_link_required:
      'Log in with your existing password once to link Google. Both sign-in methods will keep working.',
    account_link_conflict:
      'Google could not be linked to this account. Start again with Google sign-in.',
    account_link_expired: 'The Google linking request expired. Start again with Google sign-in.',
    email_verification_required:
      'Verify the email address separately before using that Google account.',
    invalid_invite: 'That invitation is invalid, expired, or has already been used.',
    verification_expired: 'That verification link is invalid, expired, or has already been used.',
    verification_password_required:
      'Enter the password you chose at signup to finish on this device.',
    verification_wrong_password: 'Wrong password. Enter the password you chose at signup.',
    oauth_failed: 'Could not sign in with Google. Please try again.',
    oauth_unavailable: 'Google sign-in is not available right now.',
    server_unavailable: 'The service is unavailable right now. Please try again shortly.',
    logout_incomplete:
      'This device may still be logged in when the app starts again. If it is, log out again.',
    generic: 'Something went wrong. Please try again.',
  },
};

function withAccountLinkErrors(copy: AuthStrings): AuthStrings {
  return {
    ...copy,
    errors: {
      ...copy.errors,
      account_link_conflict: copy.errors.account_link_conflict ?? copy.errors.oauth_failed,
      account_link_expired: copy.errors.account_link_expired ?? copy.errors.expired,
    },
  };
}

const localizedEuAuthStrings = Object.fromEntries(
  Object.entries(EU_AUTH_STRINGS).map(([language, copy]) => [
    language,
    withAccountLinkErrors(copy),
  ]),
) as Record<Exclude<EuAppLanguage, 'en' | 'sv'>, AuthStrings>;

export const AUTH_STRINGS = {
  ...localizedEuAuthStrings,
  sv: withAccountLinkErrors(sv),
  en: withAccountLinkErrors(en),
} satisfies Record<EuAppLanguage, AuthStrings>;
export type AuthLang = keyof typeof AUTH_STRINGS;

export function authLangFor(locale: string): AuthLang {
  let language = 'en';
  try {
    language = new Intl.Locale(locale || 'en').language;
  } catch {
    // Invalid settings use the complete English catalog.
  }
  return Object.hasOwn(AUTH_STRINGS, language) ? (language as AuthLang) : 'en';
}
